import { useState, useRef, useEffect } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTimes, faPaperPlane, faHeadphones } from "@fortawesome/free-solid-svg-icons";
import { useNavigate } from "react-router-dom";
import "./AIBot.css";

const AIBot = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [messages, setMessages] = useState([
    {
      id: 1,
      text: "Hi! 👋 I'm InfrecAI. How can I help you today?",
      sender: "bot",
      timestamp: new Date()
    }
  ]);
  const [inputValue, setInputValue] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [showContactForm, setShowContactForm] = useState(false);
  const [contactForm, setContactForm] = useState({
    name: "",
    email: "",
    phone: "",
    subject: "",
    message: ""
  });
  const messagesEndRef = useRef(null);
  const navigate = useNavigate();

  const WHATSAPP_NUMBER = "919279123466";
  const WHATSAPP_URL = `https://wa.me/${WHATSAPP_NUMBER}`;
  const PHONE_NUMBER = "+91 9279123466";

  const qaDatabase = {
    "life insurance": {
      answer: "Life insurance is a contract between you and an insurance company. In exchange for regular premium payments, the insurer pays a sum of money to your beneficiaries when you pass away. It provides financial security for your loved ones.",
      action: "life"
    },
    "health insurance": {
      answer: "Health insurance covers medical expenses including doctor visits, hospital stays, and medications. It helps protect you from high medical costs and ensures access to quality healthcare.",
      action: "health"
    },
    "term life": {
      answer: "Term life insurance provides coverage for a specific period (10-30 years) at lower premiums. It's ideal for those seeking temporary coverage at a lower cost.",
      action: "life"
    },
    "whole life": {
      answer: "Whole life insurance covers you for your entire life and includes a cash value component that grows over time. It has higher premiums but provides lifetime protection and savings benefits.",
      action: "life"
    },
    "claim": {
      answer: "To file a claim, contact our customer service team with your policy number and required documents. You can call our helpline, visit our office, or submit documents online. Our claims team will guide you through the process.",
      action: "contact"
    },
    "quote": {
      answer: "You can get a free quote by visiting our services page. Select the insurance type you're interested in and fill out the form. Our team will provide personalized quotes based on your age and health.",
      action: "services"
    },
    "apply": {
      answer: "You can apply for insurance through our website. Visit the services page, select your preferred insurance type, and complete the application form. Our team will review your application within 2-3 business days.",
      action: "services"
    },
    "premium": {
      answer: "Premium is the amount you pay regularly (monthly, quarterly, or annually) to maintain your insurance coverage. The premium amount depends on factors like age, health, coverage amount, and policy type.",
      action: null
    },
    "deductible": {
      answer: "A deductible is the amount you agree to pay out of pocket before your insurance company covers the remaining cost of a claim. Higher deductibles usually mean lower premiums.",
      action: null
    },
    "coverage": {
      answer: "Coverage refers to the amount of financial protection your insurance policy provides. It's the maximum amount the insurance company will pay for a claim.",
      action: null
    },
    "contact": {
      answer: `You can reach our team through our contact page or send us a message on WhatsApp. Choose the option that works best for you. You can also call us at ${PHONE_NUMBER}.`,
      action: "contact"
    },
    "faq": {
      answer: "Visit our FAQ page to find answers to common questions about insurance, claims, and our services.",
      action: "faqs"
    },
    "help": {
      answer: "I'm here to help! You can ask me about life insurance, health insurance, claims, quotes, or how to apply. What would you like to know?",
      action: null
    },
    "product": {
      answer: "We offer Life Insurance, Health Insurance, Mutual Funds, and Fixed Deposits. Life Insurance provides financial security for your loved ones, Health Insurance covers medical expenses, Mutual Funds help you invest and grow wealth, and Fixed Deposits offer guaranteed returns. Which one interests you?",
      action: "services"
    },
    "which product": {
      answer: "We offer Life Insurance, Health Insurance, Mutual Funds, and Fixed Deposits. Life Insurance provides financial security for your loved ones, Health Insurance covers medical expenses, Mutual Funds help you invest and grow wealth, and Fixed Deposits offer guaranteed returns. Which one interests you?",
      action: "services"
    },
    "best product": {
      answer: "The best product depends on your needs. Life Insurance protects your family, Health Insurance covers medical costs, Mutual Funds help grow wealth through investments, and Fixed Deposits provide safe, guaranteed returns. We recommend a mix of these for complete financial security. Would you like to know more?",
      action: "services"
    },
    "buy insurance": {
      answer: "You can buy Life Insurance, Health Insurance, Mutual Funds, and Fixed Deposits through our website. Visit the services page to explore all our products. Our team will help you choose the right options based on your needs and budget.",
      action: "services"
    },
    "which insurance should i buy": {
      answer: "The right products depend on your situation. Life Insurance protects your family, Health Insurance covers medical costs, Mutual Funds help grow wealth, and Fixed Deposits offer safe returns. We recommend getting a combination for complete financial protection. Visit our services page to explore options.",
      action: "services"
    },
    "mutual funds": {
      answer: "Mutual Funds are investment products that pool money from multiple investors to invest in stocks, bonds, and other securities. They help you grow your wealth over time with professional management. Visit our services page to explore our Mutual Fund options.",
      action: "services"
    },
    "fixed deposit": {
      answer: "Fixed Deposits are safe investment products that offer guaranteed returns over a fixed period. Your money earns interest at a predetermined rate. They are ideal for conservative investors seeking stable returns. Visit our services page to learn more about our Fixed Deposit plans.",
      action: "services"
    }
  };

  const suggestedQuestions = [
    "What is life insurance?",
    "Tell me about health insurance",
    "What are Mutual Funds?",
    "Tell me about Fixed Deposits",
    "Which product should I buy?",
    "How do I apply?"
  ];

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    const handleSidebarToggle = (event) => {
      setIsSidebarOpen(event.detail.isOpen);
    };

    window.addEventListener('sidebarToggle', handleSidebarToggle);
    return () => window.removeEventListener('sidebarToggle', handleSidebarToggle);
  }, []);

  const findAnswer = (userMessage) => {
    const lowerMessage = userMessage.toLowerCase();
    
    for (const [keyword, data] of Object.entries(qaDatabase)) {
      if (lowerMessage.includes(keyword)) {
        return data;
      }
    }
    
    return {
      answer: "I'm not sure about that. Could you please rephrase your question or choose from the suggested topics? I'm here to help with insurance-related questions.",
      action: null
    };
  };

  const handleSendMessage = async (text = inputValue) => {
    if (!text.trim()) return;

    const userMessage = {
      id: messages.length + 1,
      text: text,
      sender: "user",
      timestamp: new Date()
    };

    setMessages(prev => [...prev, userMessage]);
    setInputValue("");
    setIsLoading(true);

    setTimeout(() => {
      const result = findAnswer(text);
      const botMessage = {
        id: messages.length + 2,
        text: result.answer,
        sender: "bot",
        timestamp: new Date(),
        action: result.action
      };
      setMessages(prev => [...prev, botMessage]);
      setIsLoading(false);
    }, 800);
  };

  const handleSuggestedQuestion = (question) => {
    handleSendMessage(question);
  };

  const handleNavigate = (path) => {
    navigate(path);
    setIsOpen(false);
  };

  const handleContactFormChange = (e) => {
    const { name, value } = e.target;
    setContactForm(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleContactFormSubmit = (e) => {
    e.preventDefault();

    if (!contactForm.name || !contactForm.email || !contactForm.phone || !contactForm.message) {
      alert("Please fill all fields");
      return;
    }

    // Create formatted message for WhatsApp
    const whatsappMessage = `*New Inquiry from INFREC Website*\n\n*Name:* ${contactForm.name}\n*Email:* ${contactForm.email}\n*Phone:* ${contactForm.phone}\n*Subject:* ${contactForm.subject || "General Inquiry"}\n\n*Message:*\n${contactForm.message}`;
    
    // Encode message for URL
    const encodedMessage = encodeURIComponent(whatsappMessage);
    const whatsappLink = `${WHATSAPP_URL}?text=${encodedMessage}`;
    
    // Open WhatsApp
    window.open(whatsappLink, '_blank');
    
    // Show confirmation message
    const botMessage = {
      id: messages.length + 1,
      text: `Thank you ${contactForm.name}! Your details have been sent to our WhatsApp. We'll get back to you shortly at ${contactForm.phone}.`,
      sender: "bot",
      timestamp: new Date()
    };
    
    setMessages(prev => [...prev, botMessage]);
    setContactForm({ name: "", email: "", phone: "", subject: "", message: "" });
    setShowContactForm(false);
  };

  const handleClearChat = () => {
    setMessages([
      {
        id: 1,
        text: "Hi! 👋 I'm InfrecAI. How can I help you today?",
        sender: "bot",
        timestamp: new Date()
      }
    ]);
    setShowContactForm(false);
  };

  return (
    <>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={`ai-bot-button ${isSidebarOpen ? 'sidebar-open' : ''}`}
        title="Chat with INFREC AI"
      >
        <FontAwesomeIcon icon={faHeadphones} />
      </button>

      {isOpen && (
        <div className={`ai-bot-wrapper ${isSidebarOpen ? 'sidebar-open' : ''}`}>
          <div className="ai-bot-chat-container">
            <div className="ai-bot-chat-header">
              <div className="ai-bot-header-content">
                <div className="ai-bot-logo-section">
                  <FontAwesomeIcon icon={faHeadphones} className="ai-bot-avatar-img" />
                  <div>
                    <h3 className="ai-bot-title">InfrecAI</h3>
                    <p className="ai-bot-status">Always here to help</p>
                  </div>
                </div>
              </div>
              <div className="ai-bot-header-actions">
                <button
                  onClick={handleClearChat}
                  className="ai-bot-clear-btn"
                  title="Clear chat"
                >
                  ↻
                </button>
                <button
                  onClick={() => setIsOpen(false)}
                  className="ai-bot-close"
                >
                  <FontAwesomeIcon icon={faTimes} />
                </button>
              </div>
            </div>

            <div className="ai-bot-messages">
              {showContactForm ? (
                <form onSubmit={handleContactFormSubmit} className="ai-bot-contact-form">
                  <h4 className="ai-bot-form-title">Send us a message</h4>
                  <p className="ai-bot-form-subtitle">Fill the form and we&apos;ll reply via WhatsApp</p>
                  
                  <div className="ai-bot-form-group">
                    <label>Name *</label>
                    <input
                      type="text"
                      name="name"
                      value={contactForm.name}
                      onChange={handleContactFormChange}
                      placeholder="Your full name"
                      required
                    />
                  </div>

                  <div className="ai-bot-form-group">
                    <label>Email *</label>
                    <input
                      type="email"
                      name="email"
                      value={contactForm.email}
                      onChange={handleContactFormChange}
                      placeholder="your@email.com"
                      required
                    />
                  </div>

                  <div className="ai-bot-form-group">
                    <label>Phone *</label>
                    <input
                      type="tel"
                      name="phone"
                      value={contactForm.phone}
                      onChange={handleContactFormChange}
                      placeholder="Your phone number"
                      required
                    />
                  </div>

                  <div className="ai-bot-form-group">
                    <label>Subject</label>
                    <input
                      type="text"
                      name="subject"
                      value={contactForm.subject}
                      onChange={handleContactFormChange}
                      placeholder="What is this about?"
                    />
                  </div>

                  <div className="ai-bot-form-group">
                    <label>Message *</label>
                    <textarea
                      name="message"
                      value={contactForm.message}
                      onChange={handleContactFormChange}
                      placeholder="Tell us more..."
                      rows="3"
                      required
                    ></textarea>
                  </div>

                  <div className="ai-bot-form-buttons">
                    <button type="submit" className="ai-bot-submit-btn">
                      💬 Send via WhatsApp
                    </button>
                    <button
                      type="button"
                      className="ai-bot-cancel-btn"
                      onClick={() => setShowContactForm(false)}
                    >
                      Cancel
                    </button>
                  </div>
                </form>
              ) : (
                <>
                  {messages.map((message) => (
                    <div key={message.id}>
                      <div className={`ai-bot-message ${message.sender}`}>
                        <div className="ai-bot-message-content">
                          {message.text}
                        </div>
                      </div>
                      {message.action && (
                        <div className="ai-bot-action-buttons">
                          {message.action === "life" && (
                            <>
                              <button
                                className="ai-bot-action-btn"
                                onClick={() => handleNavigate("/services")}
                              >
                                View Insurance Plans
                              </button>
                              <button
                                className="ai-bot-action-btn ai-bot-whatsapp-btn"
                                onClick={() => setShowContactForm(true)}
                              >
                                💬 Send Inquiry
                              </button>
                            </>
                          )}
                          {message.action === "health" && (
                            <>
                              <button
                                className="ai-bot-action-btn"
                                onClick={() => handleNavigate("/services")}
                              >
                                View Insurance Plans
                              </button>
                              <button
                                className="ai-bot-action-btn ai-bot-whatsapp-btn"
                                onClick={() => setShowContactForm(true)}
                              >
                                💬 Send Inquiry
                              </button>
                            </>
                          )}
                          {message.action === "services" && (
                            <>
                              <button
                                className="ai-bot-action-btn"
                                onClick={() => handleNavigate("/services")}
                              >
                                Go to Services
                              </button>
                              <button
                                className="ai-bot-action-btn ai-bot-whatsapp-btn"
                                onClick={() => setShowContactForm(true)}
                              >
                                💬 Send Inquiry
                              </button>
                            </>
                          )}
                          {message.action === "contact" && (
                            <>
                              <button
                                className="ai-bot-action-btn"
                                onClick={() => handleNavigate("/contact")}
                              >
                                Go to Contact Page
                              </button>
                              <button
                                className="ai-bot-action-btn ai-bot-whatsapp-btn"
                                onClick={() => setShowContactForm(true)}
                              >
                                💬 Send Inquiry
                              </button>
                            </>
                          )}
                          {message.action === "faqs" && (
                            <button
                              className="ai-bot-action-btn"
                              onClick={() => handleNavigate("/faqs")}
                            >
                              View FAQs
                            </button>
                          )}
                        </div>
                      )}
                    </div>
                  ))}
                  {isLoading && (
                    <div className="ai-bot-message bot">
                      <div className="ai-bot-message-content">
                        <div className="ai-bot-typing">
                          <span></span>
                          <span></span>
                          <span></span>
                        </div>
                      </div>
                    </div>
                  )}
                </>
              )}
              <div ref={messagesEndRef} />
            </div>

            {messages.length === 1 && !showContactForm && (
              <div className="ai-bot-suggestions">
                <p className="ai-bot-suggestions-title">Suggested topics:</p>
                <div className="ai-bot-suggestions-grid">
                  {suggestedQuestions.map((question, index) => (
                    <button
                      key={index}
                      className="ai-bot-suggestion-btn"
                      onClick={() => handleSuggestedQuestion(question)}
                    >
                      {question}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {!showContactForm && (
              <div className="ai-bot-input-area">
                <input
                  type="text"
                  value={inputValue}
                  onChange={(e) => setInputValue(e.target.value)}
                  onKeyPress={(e) => e.key === "Enter" && handleSendMessage()}
                  placeholder="Type your question..."
                  className="ai-bot-input"
                  disabled={isLoading}
                />
                <button
                  onClick={() => handleSendMessage()}
                  className="ai-bot-send-btn"
                  disabled={isLoading || !inputValue.trim()}
                >
                  <FontAwesomeIcon icon={faPaperPlane} />
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
};

export default AIBot;
